import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Avatar, AvatarFallback } from '../../../components/ui/avatar';
import { Badge } from '../../../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../../../components/ui/dialog';
import { User, Lock, CheckCircle, Loader2, Upload, Eye, EyeOff, Bell, ArrowUp } from 'lucide-react';
import { useProfile } from '../../../hooks/useProfile';
import { useSnackbar } from 'notistack';
import { useChangePassword } from '../../../api/auth/use-customer-auth';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import KycStatus from './components/KycStatus';
import KycUploadForm from './components/KycUploadForm';
import { CountrySelect } from '../../../components/ui/country-select';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../components/ui/select';
import { cn } from '../../../lib/utils';
import NotificationPreferencesCard from './components/NotificationPreferencesCard';
import { useTradingAccounts } from '@/api/trading-accounts/use-trading-accounts';
import {
    useCreateTradingAccountWithdrawal,
    usePaymentMethods,
} from '@/api/transactions/use-trading-account-transactions';
import TradingAccountWithdrawalForm from '@/pages/portal/wallet/components/TradingAccountWithdrawalForm';

interface FormErrors {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    address?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
}

const ProfilePage: React.FC = () => {
    const { t } = useTranslation('profile');
    const { profile, updateProfile, isLoading } = useProfile();
    const { enqueueSnackbar } = useSnackbar();
    const changePasswordMutation = useChangePassword();

    const [showPasswordDialog, setShowPasswordDialog] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
    const [showAccountSelectionDialog, setShowAccountSelectionDialog] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState<any>(null);
    const [errors, setErrors] = useState<FormErrors>({});

    // Trading accounts and payment methods for withdrawal
    const { data: tradingAccountsData } = useTradingAccounts();
    const { data: paymentMethodsData } = usePaymentMethods();
    const createWithdrawal = useCreateTradingAccountWithdrawal();

    const tradingAccounts = tradingAccountsData?.accounts || [];
    const paymentMethods = paymentMethodsData?.data || [];

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        country: '',
        address: '',
        city: '',
        state: '',
        postalCode: '',
    });

    // Update form data when profile changes
    React.useEffect(() => {
        setFormData({
            firstName: profile.firstName,
            lastName: profile.lastName,
            email: profile.email,
            phoneNumber: profile.phoneNumber || '',
            country: profile.country || '',
            address: profile.address || '',
            city: profile.city || '',
            state: profile.state || '',
            postalCode: profile.postalCode || '',
        });
    }, [profile]);

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        // Required fields
        if (!formData.firstName.trim()) {
            newErrors.firstName = t('firstNameRequired');
        } else if (formData.firstName.trim().length < 2) {
            newErrors.firstName = t('firstNameMinLength');
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = t('lastNameRequired');
        } else if (formData.lastName.trim().length < 2) {
            newErrors.lastName = t('lastNameMinLength');
        }

        if (!formData.email.trim()) {
            newErrors.email = t('emailRequired');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = t('emailInvalid');
        }

        if (
            formData.phoneNumber &&
            !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phoneNumber.replace(/\s/g, ''))
        ) {
            newErrors.phoneNumber = t('phoneInvalid');
        }

        if (formData.address && formData.address.trim().length < 5) {
            newErrors.address = t('addressMinLength');
        }

        if (formData.city && formData.city.trim().length < 2) {
            newErrors.city = t('cityMinLength');
        }

        if (formData.state && formData.state.trim().length < 2) {
            newErrors.state = t('stateMinLength');
        }

        if (formData.postalCode && formData.postalCode.trim().length < 3) {
            newErrors.postalCode = t('postalCodeMinLength');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Clear error when user starts typing
        if (errors[name as keyof FormErrors]) {
            setErrors(prev => ({ ...prev, [name]: undefined }));
        }
    };

    const handleCountryChange = (value: string) => {
        setFormData(prev => ({ ...prev, country: value }));
    };

    const handleProfileSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            enqueueSnackbar(t('fixFormErrors'), { variant: 'error' });
            return;
        }

        try {
            await updateProfile(formData);
            enqueueSnackbar(t('profileUpdated'), { variant: 'success' });
        } catch (error) {
            // Error is already handled in the hook
        }
    };

    const handlePasswordChange = async (e: React.FormEvent) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            enqueueSnackbar(t('passwordsDoNotMatch'), { variant: 'error' });
            return;
        }

        if (newPassword.length < 8) {
            enqueueSnackbar(t('passwordMinLength'), { variant: 'error' });
            return;
        }

        try {
            await changePasswordMutation.mutateAsync({
                current_password: currentPassword,
                password: newPassword,
                password_confirmation: confirmPassword,
            });

            enqueueSnackbar(t('passwordChangedSuccess'), { variant: 'success' });
            setShowPasswordDialog(false);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || t('passwordChangeFailed');
            enqueueSnackbar(errorMessage, { variant: 'error' });
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-background flex justify-center items-center">
                <Loader2 className="h-8 w-8 animate-spin text-foreground" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background p-6">
            <div className="max-w-6xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <Avatar className="h-16 w-16">
                        <AvatarFallback className="text-xl bg-primary text-primary-foreground">
                            {profile.firstName?.[0]}
                            {profile.lastName?.[0]}
                        </AvatarFallback>
                    </Avatar>
                    <div>
                        <h1 className="text-3xl font-bold text-foreground">
                            {profile.firstName} {profile.lastName}
                        </h1>
                        <p className="text-muted-foreground">
                            {t('memberSince')}{' '}
                            {profile.createdAt ? (
                                <DateTimeFormatter date={profile.createdAt} />
                            ) : (
                                'N/A'
                            )}
                        </p>
                    </div>
                </div>

                {/* Cards Layout - Stacked Vertically */}
                <div className="space-y-6 max-w-2xl">
                    {/* Personal Information Card */}
                    <Card className="bg-card border-border">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-foreground">
                                <User className="h-5 w-5" />
                                {t('personalInformation')}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleProfileSubmit} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="firstName" className="text-foreground">
                                            {t('firstName')}
                                        </Label>
                                        <Input
                                            id="firstName"
                                            name="firstName"
                                            value={formData.firstName}
                                            onChange={handleChange}
                                            className={cn(
                                                'bg-background border-border text-foreground placeholder:text-muted-foreground',
                                                errors.firstName && 'border-red-500'
                                            )}
                                            required
                                        />
                                        {errors.firstName && (
                                            <p className="text-red-400 text-sm">
                                                {errors.firstName}
                                            </p>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="lastName" className="text-foreground">
                                            {t('lastName')}
                                        </Label>
                                        <Input
                                            id="lastName"
                                            name="lastName"
                                            value={formData.lastName}
                                            onChange={handleChange}
                                            className={cn(
                                                'bg-background border-border text-foreground placeholder:text-muted-foreground',
                                                errors.lastName && 'border-red-500'
                                            )}
                                            required
                                        />
                                        {errors.lastName && (
                                            <p className="text-red-400 text-sm">
                                                {errors.lastName}
                                            </p>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="email" className="text-foreground">
                                            {t('email')}
                                        </Label>
                                        <Input
                                            id="email"
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className={cn(
                                                'bg-background border-border text-foreground placeholder:text-muted-foreground',
                                                errors.email && 'border-red-500'
                                            )}
                                            required
                                        />
                                        {errors.email && (
                                            <p className="text-red-400 text-sm">{errors.email}</p>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="phoneNumber" className="text-foreground">
                                            {t('phoneNumber')}
                                        </Label>
                                        <Input
                                            id="phoneNumber"
                                            name="phoneNumber"
                                            value={formData.phoneNumber}
                                            onChange={handleChange}
                                            className={cn(
                                                'bg-background border-border text-foreground placeholder:text-muted-foreground',
                                                errors.phoneNumber && 'border-red-500'
                                            )}
                                        />
                                        {errors.phoneNumber && (
                                            <p className="text-red-400 text-sm">
                                                {errors.phoneNumber}
                                            </p>
                                        )}
                                    </div>
                                    <div className="space-y-2 md:col-span-2">
                                        <Label htmlFor="country" className="text-foreground">
                                            {t('country')}
                                        </Label>
                                        <CountrySelect
                                            value={formData.country}
                                            onValueChange={handleCountryChange}
                                            placeholder={t('selectCountry')}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="address" className="text-foreground">
                                            {t('address')}
                                        </Label>
                                        <Input
                                            id="address"
                                            name="address"
                                            value={formData.address}
                                            onChange={handleChange}
                                            placeholder={t('streetAddress')}
                                            className={cn(
                                                'bg-background border-border text-foreground',
                                                errors.address && 'border-red-500'
                                            )}
                                        />
                                        {errors.address && (
                                            <p className="text-red-400 text-sm">{errors.address}</p>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="city" className="text-foreground">
                                            {t('city')}
                                        </Label>
                                        <Input
                                            id="city"
                                            name="city"
                                            value={formData.city}
                                            onChange={handleChange}
                                            placeholder={t('cityPlaceholder')}
                                            className={cn(
                                                'bg-background border-border text-foreground',
                                                errors.city && 'border-red-500'
                                            )}
                                        />
                                        {errors.city && (
                                            <p className="text-red-400 text-sm">{errors.city}</p>
                                        )}
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="state" className="text-foreground">
                                            {t('stateRegion')}
                                        </Label>
                                        <Input
                                            id="state"
                                            name="state"
                                            value={formData.state}
                                            onChange={handleChange}
                                            placeholder={t('stateRegion')}
                                            className={cn(
                                                'bg-background border-border text-foreground',
                                                errors.state && 'border-red-500'
                                            )}
                                        />
                                        {errors.state && (
                                            <p className="text-red-400 text-sm">{errors.state}</p>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="postalCode" className="text-foreground">
                                            {t('postalZip')}
                                        </Label>
                                        <Input
                                            id="postalCode"
                                            name="postalCode"
                                            value={formData.postalCode}
                                            onChange={handleChange}
                                            placeholder={t('postalZip')}
                                            className={cn(
                                                'bg-background border-border text-foreground',
                                                errors.postalCode && 'border-red-500'
                                            )}
                                        />
                                        {errors.postalCode && (
                                            <p className="text-red-400 text-sm">
                                                {errors.postalCode}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <Button type="submit" className="w-full" disabled={isLoading}>
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                            {t('updatingProfile')}
                                        </>
                                    ) : (
                                        t('updateProfile')
                                    )}
                                </Button>
                            </form>
                        </CardContent>
                    </Card>

                    {/* KYC Verification Card */}
                    <Card className="bg-card border-border">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-foreground">
                                <CheckCircle className="h-5 w-5" />
                                {t('kycVerification')}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <KycStatus />
                            <KycUploadForm />
                        </CardContent>
                    </Card>

                    {/* Password Change Card */}
                    <Card className="bg-card border-border">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-foreground">
                                <Lock className="h-5 w-5" />
                                {t('passwordSecurity')}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="text-sm text-muted-foreground">
                                <p>{t('keepAccountSecure')}</p>
                            </div>

                            <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
                                <DialogTrigger asChild>
                                    <Button variant="outline" className="w-full">
                                        {t('changePassword')}
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="bg-background border-border">
                                    <DialogHeader>
                                        <DialogTitle className="text-foreground">
                                            {t('changePassword')}
                                        </DialogTitle>
                                    </DialogHeader>
                                    <form onSubmit={handlePasswordChange} className="space-y-4">
                                        <div className="space-y-2">
                                            <Label
                                                htmlFor="currentPassword"
                                                className="text-foreground"
                                            >
                                                {t('currentPassword')}
                                            </Label>
                                            <div className="relative">
                                                <Input
                                                    id="currentPassword"
                                                    type={showPassword ? 'text' : 'password'}
                                                    value={currentPassword}
                                                    onChange={e =>
                                                        setCurrentPassword(e.target.value)
                                                    }
                                                    className={cn(
                                                        'bg-background border-border text-foreground pr-10',
                                                        errors.currentPassword && 'border-red-500'
                                                    )}
                                                    required
                                                />
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="absolute right-0 top-0 h-full px-3 text-muted-foreground hover:text-foreground"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                >
                                                    {showPassword ? (
                                                        <EyeOff className="h-4 w-4" />
                                                    ) : (
                                                        <Eye className="h-4 w-4" />
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label
                                                htmlFor="newPassword"
                                                className="text-foreground"
                                            >
                                                {t('newPassword')}
                                            </Label>
                                            <div className="relative">
                                                <Input
                                                    id="newPassword"
                                                    type={showNewPassword ? 'text' : 'password'}
                                                    value={newPassword}
                                                    onChange={e => setNewPassword(e.target.value)}
                                                    className={cn(
                                                        'bg-background border-border text-foreground pr-10',
                                                        errors.newPassword && 'border-red-500'
                                                    )}
                                                    required
                                                />
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="absolute right-0 top-0 h-full px-3 text-muted-foreground hover:text-foreground"
                                                    onClick={() =>
                                                        setShowNewPassword(!showNewPassword)
                                                    }
                                                >
                                                    {showNewPassword ? (
                                                        <EyeOff className="h-4 w-4" />
                                                    ) : (
                                                        <Eye className="h-4 w-4" />
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label
                                                htmlFor="confirmPassword"
                                                className="text-foreground"
                                            >
                                                {t('confirmNewPassword')}
                                            </Label>
                                            <div className="relative">
                                                <Input
                                                    id="confirmPassword"
                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                    value={confirmPassword}
                                                    onChange={e =>
                                                        setConfirmPassword(e.target.value)
                                                    }
                                                    className={cn(
                                                        'bg-background border-border text-foreground pr-10',
                                                        errors.confirmPassword && 'border-red-500'
                                                    )}
                                                    required
                                                />
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="absolute right-0 top-0 h-full px-3 text-muted-foreground hover:text-foreground"
                                                    onClick={() =>
                                                        setShowConfirmPassword(!showConfirmPassword)
                                                    }
                                                >
                                                    {showConfirmPassword ? (
                                                        <EyeOff className="h-4 w-4" />
                                                    ) : (
                                                        <Eye className="h-4 w-4" />
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="flex justify-end gap-2 pt-4">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={() => setShowPasswordDialog(false)}
                                                disabled={changePasswordMutation.isPending}
                                            >
                                                {t('cancel')}
                                            </Button>
                                            <Button
                                                type="submit"
                                                disabled={changePasswordMutation.isPending}
                                            >
                                                {changePasswordMutation.isPending ? (
                                                    <>
                                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                        {t('changingPassword')}
                                                    </>
                                                ) : (
                                                    t('changePassword')
                                                )}
                                            </Button>
                                        </div>
                                    </form>
                                </DialogContent>
                            </Dialog>

                            {/* Withdraw Section */}
                            <div className="pt-4 border-t border-border">
                                <div className="text-sm text-muted-foreground mb-4">
                                    <p>{t('withdrawDescription')}</p>
                                </div>
                                <Button
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => {
                                        if (tradingAccounts.length === 0) {
                                            enqueueSnackbar(t('noTradingAccountsAvailable'), {
                                                variant: 'warning',
                                            });
                                            return;
                                        }
                                        if (tradingAccounts.length === 1) {
                                            setSelectedAccount(tradingAccounts[0]);
                                            setShowWithdrawDialog(true);
                                        } else {
                                            // If multiple accounts, show account selection dialog
                                            setShowAccountSelectionDialog(true);
                                        }
                                    }}
                                >
                                    <ArrowUp className="h-4 w-4 mr-2" />
                                    {t('withdrawFunds')}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Account Selection Dialog */}
                    <Dialog
                        open={showAccountSelectionDialog}
                        onOpenChange={setShowAccountSelectionDialog}
                    >
                        <DialogContent className="sm:max-w-[400px]">
                            <DialogHeader>
                                <DialogTitle>{t('selectTradingAccount')}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                    <Label>{t('chooseAccountToWithdraw')}</Label>
                                    <Select
                                        value={selectedAccount?.id || ''}
                                        onValueChange={value => {
                                            const account = tradingAccounts.find(
                                                acc => acc.id === value
                                            );
                                            setSelectedAccount(account);
                                        }}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder={t('selectAnAccount')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {tradingAccounts.map(account => (
                                                <SelectItem key={account.id} value={account.id}>
                                                    {account.account_number
                                                        ? `Account #${account.account_number} (${account.base_currency})`
                                                        : `${account.base_currency} Account`}
                                                    {' - '}
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: account.base_currency,
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    }).format(account.balance || 0)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex justify-end gap-2 pt-4">
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            setShowAccountSelectionDialog(false);
                                            setSelectedAccount(null);
                                        }}
                                    >
                                        {t('cancel')}
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            if (selectedAccount) {
                                                setShowAccountSelectionDialog(false);
                                                setShowWithdrawDialog(true);
                                            } else {
                                                enqueueSnackbar(t('pleaseSelectAccount'), {
                                                    variant: 'warning',
                                                });
                                            }
                                        }}
                                        disabled={!selectedAccount}
                                    >
                                        {t('continue')}
                                    </Button>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>

                    {/* Withdraw Dialog */}
                    {selectedAccount && (
                        <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
                            <DialogContent className="sm:max-w-[500px]">
                                <DialogHeader>
                                    <DialogTitle>
                                        {t('withdrawFrom')} {selectedAccount.base_currency}{' '}
                                        {t('tradingAccount')}
                                    </DialogTitle>
                                </DialogHeader>
                                <TradingAccountWithdrawalForm
                                    account={selectedAccount}
                                    paymentMethods={paymentMethods}
                                    onSubmit={data => {
                                        createWithdrawal.mutate(data, {
                                            onSuccess: () => {
                                                enqueueSnackbar(t('withdrawalSuccess'), {
                                                    variant: 'success',
                                                });
                                                setShowWithdrawDialog(false);
                                                setSelectedAccount(null);
                                            },
                                            onError: (error: any) => {
                                                enqueueSnackbar(
                                                    error?.response?.data?.message ||
                                                        t('withdrawalFailed'),
                                                    { variant: 'error' }
                                                );
                                            },
                                        });
                                    }}
                                    isLoading={createWithdrawal.isPending}
                                    onCancel={() => {
                                        setShowWithdrawDialog(false);
                                        setSelectedAccount(null);
                                    }}
                                />
                            </DialogContent>
                        </Dialog>
                    )}

                    {/* Notification Preferences Card */}
                    <Card className="bg-card border-border">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-foreground">
                                <Bell className="h-5 w-5" />
                                {t('notificationPreferences')}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-sm text-muted-foreground mb-4">
                                <p>{t('customizeNotifications')}</p>
                            </div>
                            <NotificationPreferencesCard />
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;
